import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { sceneImage, TRAITS, fmtMoney } from "@/data/gameData";
import { ArrowLeft, Loader2, Wallet, X } from "lucide-react";

const loadingLines = [
  "Escrevendo o próximo capítulo...",
  "As consequências estão tomando forma...",
  "O destino considera suas escolhas...",
  "Preparando a próxima decisão...",
];

export default function Game() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("resume");
  const storyId = searchParams.get("story");
  const navigate = useNavigate();
  const { http } = useAuth();

  const [scene, setScene] = useState(null);
  const [busy, setBusy] = useState(true);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState("");
  const [loadingLine, setLoadingLine] = useState(loadingLines[0]);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        if (resumeId) {
          const { data } = await http.get(`/game/session/${resumeId}`);
          if (data.finished) { navigate(`/resultado/${resumeId}`); return; }
          setScene(data);
        } else {
          const { data } = await http.post("/game/start", { category, story_id: storyId || undefined });
          setScene(data);
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Não consegui iniciar a história.");
      } finally {
        setBusy(false);
      }
    })();
  }, [category, http, resumeId, storyId, navigate]);

  useEffect(() => {
    if (!busy && !choosing) return;
    const id = setInterval(() => {
      setLoadingLine(loadingLines[Math.floor(Math.random() * loadingLines.length)]);
    }, 1800);
    return () => clearInterval(id);
  }, [busy, choosing]);

  const choose = async (index) => {
    if (choosing) return;
    setChoosing(true);
    try {
      const { data } = await http.post("/game/choose", { session_id: scene.session_id, option_index: index });
      if (data.finished) {
        navigate(`/resultado/${data.result.session_id}`, { state: { result: data.result } });
        return;
      }
      // consequence toasts
      if (data.money_delta) {
        toast(`${data.money_delta > 0 ? "+" : ""}${fmtMoney(data.money_delta)}`, {
          description: "Seu dinheiro mudou",
        });
      }
      const gains = Object.entries(data.trait_gain || {}).filter(([, v]) => v > 0);
      if (gains.length) {
        const t = TRAITS.find((x) => x.key === gains.sort((a, b) => b[1] - a[1])[0][0]);
        if (t) toast(`${t.emoji} +${gains.sort((a, b) => b[1] - a[1])[0][1]} ${t.label}`);
      }
      setScene({ ...scene, ...data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao processar sua escolha.");
    } finally {
      setChoosing(false);
    }
  };

  const img = sceneImage(scene?.category || category, scene?.step || 1);
  const pct = scene ? Math.round(((scene.step - 1) / scene.total) * 100) : 0;

  if (busy) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#F43F5E] mb-6" />
        <p className="font-display font-bold text-xl">Criando sua história...</p>
        <p className="text-slate-400 mt-2">{loadingLine}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-4">
        <p className="text-slate-300">{error}</p>
        <button onClick={() => navigate("/historias")} className="px-6 py-3 rounded-2xl bg-[#F43F5E] font-semibold">Voltar</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1.5 bg-white/5">
        <motion.div className="h-full bg-[#F43F5E]" animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} />
      </div>

      {/* top bar */}
      <div className="glass border-b border-white/10 sticky top-1.5 z-40">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <button data-testid="game-exit-btn" onClick={() => navigate("/")} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
            <X className="w-4 h-4" /> Sair
          </button>
          <span className="text-xs uppercase tracking-widest text-slate-400">
            Decisão {scene.step} de {scene.total}
          </span>
          <span data-testid="game-money" className="flex items-center gap-1.5 text-sm font-semibold text-[#10B981]">
            <Wallet className="w-4 h-4" /> {fmtMoney(scene.money)}
          </span>
        </div>
      </div>

      {/* scene */}
      <div className="max-w-2xl mx-auto px-5 py-8 pb-40">
        <AnimatePresence mode="wait">
          {choosing ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-24 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#F43F5E] mb-5" />
              <p className="text-slate-300">{loadingLine}</p>
            </motion.div>
          ) : (
            <motion.div key={scene.step} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              <div className="relative h-52 sm:h-60 rounded-3xl overflow-hidden border border-white/10 mb-6">
                <motion.img
                  key={img} src={img} alt=""
                  initial={{ scale: 1.12, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/40 to-transparent" />
                <div className="absolute top-4 left-5">
                  <span className="text-[10px] uppercase tracking-widest text-white/90 glass px-3 py-1 rounded-full border border-white/15">
                    Decisão {scene.step}/{scene.total}
                  </span>
                </div>
                <div className="absolute bottom-4 left-5 right-5">
                  <p className="text-xs uppercase tracking-widest text-[#F43F5E] font-semibold drop-shadow">{scene.titulo}</p>
                </div>
              </div>

              {scene.step === 1 && scene.contexto && (
                <p className="text-sm text-slate-400 italic mb-4 border-l-2 border-[#F43F5E]/50 pl-4">{scene.contexto}</p>
              )}

              <p data-testid="game-narrative" className="font-display text-2xl leading-snug tracking-tight mb-8">
                {scene.narrativa}
              </p>

              <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">O que você faz?</p>
              <div className="space-y-3">
                {scene.opcoes.map((op, i) => (
                  <motion.button
                    key={`${scene.step}-${op.texto}`} data-testid={`game-option-${i}`}
                    initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.08 }}
                    onClick={() => choose(i)}
                    className="group w-full text-left rounded-2xl p-5 bg-[#0F0F14] border border-white/10 hover:border-[#F43F5E]/60 active:scale-[0.98] transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 w-6 h-6 shrink-0 grid place-items-center rounded-full bg-white/5 border border-white/10 text-xs font-bold text-slate-400 group-hover:bg-[#F43F5E] group-hover:text-white transition-colors">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-[15px] leading-snug">{op.texto}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
